import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowRight, Play } from "lucide-react";
import { scrollToId } from "@/hooks/useLenis";
import { ease } from "./Reveal";

const SLIDES = [
  { src: "/media/hero1.webp", caption: "Stillworking Production · Chisco & Reverbero" },
  { src: "/media/hero2.webp", caption: "Joker Riddim · powered by Joker Sound" },
  { src: "/media/hero3.webp", caption: "Kumar Fyah · Sweet Reggae" },
];
const DURATION = 6000;

const Line = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-[0.08em] pt-[0.14em] -mt-[0.14em]">
    <motion.span className={`block ${className}`} initial={{ y: "115%" }} animate={{ y: "0%" }} transition={{ duration: 1.2, ease, delay }}>
      {children}
    </motion.span>
  </span>
);

export function Hero() {
  const ref = useRef(null);
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  useEffect(() => {
    if (paused) return undefined;
    const t = setTimeout(() => setI((v) => (v + 1) % SLIDES.length), DURATION);
    return () => clearTimeout(t);
  }, [i, paused]);

  const go = (d) => setI((v) => (v + d + SLIDES.length) % SLIDES.length);

  return (
    <section id="top" ref={ref} data-testid="hero-photo-slider" className="relative min-h-[100svh] overflow-hidden" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        {SLIDES.map((s, k) => (
          <motion.img
            key={s.src}
            src={s.src}
            alt={s.caption}
            style={SLIDES.length === 1 ? { objectPosition: "50% 30%" } : undefined}
            className="absolute inset-0 h-full w-full object-cover"
            initial={false}
            animate={{ opacity: k === i ? 1 : 0, scale: k === i ? 1 : 1.12 }}
            transition={{ opacity: { duration: 1.4, ease: "easeInOut" }, scale: { duration: DURATION / 1000 + 1.4, ease: "linear" } }}
          />
        ))}
        <div className="absolute inset-0 bg-[#0B0B0C]/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C] via-[#0B0B0C]/30 to-[#0B0B0C]/60" />
      </motion.div>

      <motion.div style={{ y: textY }} className="relative z-10 mx-auto flex min-h-[100svh] max-w-[1440px] flex-col justify-end px-5 pb-36 pt-28 md:px-10 md:pb-40">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 1 }} className="eyebrow mb-6">
          Reggae · Dancehall · Hip Hop
        </motion.p>
        <h1 className="font-display text-[19vw] font-black uppercase leading-[0.82] tracking-[-0.01em] sm:text-[15vw] lg:text-[clamp(6rem,19vh,11rem)]" data-testid="hero-title">
          <Line delay={0.35}>Riddims</Line>
          <Line delay={0.5} className="flex items-baseline gap-[0.15em]">
            <span className="font-serif-i text-[0.62em] font-normal normal-case tracking-normal text-[var(--gold)]">for a</span> New
          </Line>
          <Line delay={0.65} className="text-outline">Wave.</Line>
        </h1>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 1, ease }} className="mt-8 flex max-w-xl flex-col gap-6 sm:flex-row sm:items-center">
          <p className="text-sm leading-relaxed text-[var(--stone)] md:text-base">
            Producers and soundboys for over twenty years. New riddims in the spirit of Jamaican classics.
          </p>
          <button data-testid="hero-cta-listen-button" onClick={() => scrollToId("releases")} className="group flex shrink-0 items-center gap-3 rounded-full bg-[var(--gold)] py-3 pl-3 pr-6 font-mono text-xs uppercase tracking-[0.2em] text-[var(--ink)] transition-transform hover:scale-[1.03] active:scale-95">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--ink)] text-[var(--gold)] transition-transform duration-500 group-hover:rotate-[360deg]"><Play size={13} fill="currentColor" /></span>
            Listen to the releases
          </button>
        </motion.div>
      </motion.div>

      {SLIDES.length > 1 && (
        <div className="absolute inset-x-0 bottom-0 z-20 mx-auto flex max-w-[1440px] items-end justify-between gap-6 px-5 pb-6 md:px-10">
        <div className="flex-1">
          <div className="mb-3 flex items-baseline gap-3 font-mono text-xs uppercase tracking-[0.2em] text-[var(--stone)]">
            <span className="text-[var(--bone)]" data-testid="hero-slide-counter">{String(i + 1).padStart(2, "0")}</span>/ {String(SLIDES.length).padStart(2, "0")}
            <span className="hidden truncate sm:inline">— {SLIDES[i].caption}</span>
          </div>
          <div className="flex max-w-md gap-2">
            {SLIDES.map((s, k) => (
              <button key={s.src} data-testid={`hero-slide-dot-${k}`} onClick={() => setI(k)} className="relative h-[3px] flex-1 overflow-hidden rounded bg-white/20" aria-label={`Slide ${k + 1}`}>
                {k === i && <motion.span key={`${i}-${paused}`} className="absolute inset-y-0 left-0 bg-[var(--gold)]" initial={{ width: "0%" }} animate={{ width: paused ? "100%" : "100%" }} transition={{ duration: paused ? 0.3 : DURATION / 1000, ease: "linear" }} />}
                {k < i && <span className="absolute inset-0 bg-white/50" />}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <button data-testid="hero-slider-prev-btn" onClick={() => go(-1)} className="flex h-11 w-11 items-center justify-center rounded-full border border-white/25 backdrop-blur transition-colors hover:border-[var(--gold)] hover:text-[var(--gold)]" aria-label="Previous"><ArrowLeft size={16} /></button>
          <button data-testid="hero-slider-next-btn" onClick={() => go(1)} className="flex h-11 w-11 items-center justify-center rounded-full border border-white/25 backdrop-blur transition-colors hover:border-[var(--gold)] hover:text-[var(--gold)]" aria-label="Next"><ArrowRight size={16} /></button>
        </div>
      </div>
      )}
    </section>
  );
}
